import React, { useEffect, useState } from 'react';
import {  Link, useSearchParams, useNavigate } from "react-router-dom";
import { PackageSearch, Check } from 'lucide-react';
import dholakImage from './assets/dholak.png';

function DealsSection({ user, accessToken, selectedType, productTypes = [], onAddToCart, onRequireLogin, onAuthError }) {

  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  
  const searchQuery = searchParams.get('search') || '';
  // 1. Read the material subcategory parameter ('kansa' or 'brass')
  const materialFilter = searchParams.get('material')?.toLowerCase() || null;

  // State for Sort Order ('asc' for Low to High, 'desc' for High to Low)
  const [sortOrder] = useState('desc');

  const [buttonFeedback, setButtonFeedback] = useState({});

  useEffect(() => {
    setLoading(true);
    let url = `${process.env.REACT_APP_API_URL}/api/products/products/`;
    const params = new URLSearchParams();

    if (selectedType && selectedType !== 'All') {
      params.append('product_type', selectedType);
    }
    if (searchQuery) {
      params.append('search', searchQuery);
    }

    const queryString = params.toString();
    if (queryString) url += `?${queryString}`;

    fetch(url)
      .then(res => res.json())
      .then(data => {
        setProducts(Array.isArray(data) ? data : (data?.results || []));
        setLoading(false);
      })
      .catch(() => {
        setProducts([]);
        setLoading(false);
      });
  }, [selectedType, searchQuery]);

  const activeCategory = productTypes.find(
    (type) => String(type?.type_id) === String(selectedType)
  );
  const dynamicCategoryName = activeCategory ? activeCategory.type_name : selectedType;

  // 2. Client-side title filter for Kansa/Brass
  const filteredProducts = products.filter((product) => {
    if (!materialFilter) return true;
    const title = (product.product_name || product.title || product.name || '').toLowerCase();
    return title.includes(materialFilter);
  });

  // Helper to extract numerical price
  const getEffectivePrice = (product) => {
    const rawPrice = product.final_price || product.price || 0;
    return typeof rawPrice === 'string'
      ? parseFloat(rawPrice.replace(/[^0-9.-]+/g, ''))
      : Number(rawPrice);
  };

  // 3. Sort over filteredProducts instead of products
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    const priceA = getEffectivePrice(a);
    const priceB = getEffectivePrice(b);

    if (sortOrder === 'asc') return priceA - priceB;
    if (sortOrder === 'desc') return priceB - priceA;
    return 0;
  });

  const handleMaterialChange = (material) => {
    const newParams = new URLSearchParams(searchParams);
    if (material) {
      newParams.set('material', material);
    } else {
      newParams.delete('material');
    }
    setSearchParams(newParams);
  };

  const handleAddToCart = (product) => {
    if (!user) {
      if (onRequireLogin) {
        onRequireLogin();
      } else {
        console.warn("onRequireLogin prop missing on this route! Falling back to home redirect...");
        navigate('/login');
      }
      return;
    }
    if (!accessToken) return;

    setButtonFeedback(prev => ({ ...prev, [product.id]: 'adding' }));

    const payload = {
      product: product.id,
      quantity: 1,
      size: product.size || 'Standard'
    };

    fetch(`${process.env.REACT_APP_API_URL}/api/products/cart/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify(payload)
    })
      .then(async res => {
        if (res.status === 401) {
          setButtonFeedback(prev => ({ ...prev, [product.id]: 'idle' }));
          onAuthError && onAuthError();
          return;
        }
        const text = await res.text();
        try {
          const data = JSON.parse(text);
          onAddToCart(product, data);

          setButtonFeedback(prev => ({ ...prev, [product.id]: 'added' }));
          setTimeout(() => {
            setButtonFeedback(prev => ({ ...prev, [product.id]: 'idle' }));
          }, 2000);

        } catch (e) {
          console.error(e);
          setButtonFeedback(prev => ({ ...prev, [product.id]: 'idle' }));
        }
      })
      .catch(err => {
        console.error(err);
        setButtonFeedback(prev => ({ ...prev, [product.id]: 'idle' }));
      });
  };

  const getDiscountPercent = (price, finalPrice) => {
    if (!price || !finalPrice) return 0;
    return Math.round(((price - finalPrice) / price) * 100);
  };

  // Check if current category is Kartals (type_id 9 or name Kartals)
  const isKartalsCategory = String(selectedType) === '9' || String(dynamicCategoryName).toLowerCase() === 'kartals';

  if (loading) {
    return (
      <div className="text-center py-32 text-[#A6A29A] uppercase tracking-widest text-[11px] font-semibold bg-[#F2F1EC]">
        Syncing Premium Inventory...
      </div>
    );
  }

  return (
    <div className="w-full bg-[#F2F1EC] min-h-screen py-6">
      <section className="max-w-7xl mx-auto">

        {/* Header Block Section */}
        <div className="mx-4 mb-6 p-6 bg-[#FDFDFB] border border-[#E1DFD7] rounded-md shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          {searchQuery ? (
            <div>
              <p className="text-[10px] text-[#A6A29A] uppercase tracking-widest font-bold">Curated Queries</p>
              <h2 className="text-[#2C2A27] text-lg font-light mt-0.5">
                Showing results for <span className="font-medium italic text-[#9A7B56]">“{searchQuery}”</span>
              </h2>
            </div>
          ) : (
            <div>
              <h2 className="text-[#2C2A27] text-sm uppercase tracking-widest font-semibold text-[#9A7B56]">
                {selectedType && selectedType !== 'All' ? `${dynamicCategoryName} ` : 'Mridangas Musical Instruments'}
              </h2>
              <p className="text-xs text-[#8A867E] font-light mt-0.5">
                Available Instruments: {sortedProducts.length} units
              </p>
            </div>
          )}

          {/* 4. Subcategory Filter Buttons for Kartals */}
          {isKartalsCategory && (
            <div className="flex items-center gap-1.5 self-start sm:self-auto">
              <span className="text-[10px] uppercase tracking-wider font-bold text-[#8A867E] mr-1">
                Material:
              </span>
              <button
                onClick={() => handleMaterialChange(null)}
                className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-xs transition-colors cursor-pointer ${
                  !materialFilter
                    ? 'bg-[#9A7B56] text-white'
                    : 'bg-[#F2F1EC] text-[#2C2A27] border border-[#E1DFD7] hover:bg-[#E1DFD7]'
                }`}
              >
                All
              </button>
              <button
                onClick={() => handleMaterialChange('kansa')}
                className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-xs transition-colors cursor-pointer ${
                  materialFilter === 'kansa'
                    ? 'bg-[#9A7B56] text-white'
                    : 'bg-[#F2F1EC] text-[#2C2A27] border border-[#E1DFD7] hover:bg-[#E1DFD7]'
                }`}
              >
                Kansa
              </button>
              <button
                onClick={() => handleMaterialChange('brass')}
                className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-xs transition-colors cursor-pointer ${
                  materialFilter === 'brass'
                    ? 'bg-[#9A7B56] text-white'
                    : 'bg-[#F2F1EC] text-[#2C2A27] border border-[#E1DFD7] hover:bg-[#E1DFD7]'
                }`}
              >
                Brass
              </button>
            </div>
          )}
        </div>

        {/* Empty State Card */}
        {sortedProducts.length === 0 ? (
          <div className="text-center py-16 bg-[#FDFDFB] border border-[#E1DFD7] rounded-md max-w-sm mx-auto p-6 space-y-3 shadow-xs">
            <PackageSearch className="w-6 h-6 text-[#A6A29A] mx-auto font-light" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#5A5750]">Collection Unpopulated</h3>
            <p className="text-xs text-[#8A867E] leading-relaxed font-light">
              We currently do not have stock listing parameters matching this material setup.
            </p>
          </div>
        ) : (
          /* Products Grid Layout Block */
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 px-4">
            {sortedProducts.map((product) => {
              const hasDiscount = product.discounted_price && parseFloat(product.discounted_price) < parseFloat(product.price);
              const discountPercent = getDiscountPercent(parseFloat(product.price), parseFloat(product.final_price));
              const currentStatus = buttonFeedback[product.id] || 'idle';

              return (
                <div key={product.id} className="relative bg-white border border-[#E1DFD7] rounded-md p-4 flex flex-col justify-between group">

                  <div>
                    <Link to={`/product/${product.id}`} className="block relative overflow-hidden rounded-sm bg-[#FDFDFB]">
                      <img
                        src={product.photo || dholakImage}
                        alt={product.product_name}
                        className={`w-full h-48 object-cover transition-transform duration-500 group-hover:scale-103 ${!product.is_active ? 'opacity-40 grayscale' : ''}`}
                      />

                      {product.is_active && hasDiscount && (
                        <span className="absolute top-2 left-2 bg-[#9A7B56] text-white text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-xs">
                          Save {discountPercent}%
                        </span>
                      )}

                      {!product.is_active && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/10 backdrop-blur-xs">
                          <span className="bg-[#2C2A27] text-[#FDFDFB] text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-xs shadow-md">
                            Out of Stock
                          </span>
                        </div>
                      )}
                    </Link>

                    <div className="mt-4 space-y-1">
                      <Link to={`/product/${product.id}`} className="block group-hover:text-[#9A7B56] transition-colors">
                        <h3 className="text-xs font-semibold text-[#2C2A27] line-clamp-1">{product.product_name}</h3>
                      </Link>

                      <div className="flex items-baseline gap-2">
                        <p className="text-xs font-mono font-semibold text-[#5A5750]">₹{product.final_price || product.price}</p>
                        {hasDiscount && (
                          <p className="text-[10px] font-mono text-[#A6A29A] line-through">₹{product.price}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <button
                      disabled={!product.is_active || currentStatus === 'adding'}
                      onClick={() => handleAddToCart(product)}
                      className={`w-full h-9 text-[10px] font-bold uppercase tracking-widest rounded-sm transition-all flex items-center justify-center gap-1.5 cursor-pointer ${!product.is_active
                          ? 'bg-[#E1DFD7] text-[#A6A29A] !cursor-not-allowed'
                          : currentStatus === 'added'
                            ? 'bg-[#FDFDFB] text-[#2C2A27] border border-[#E1DFD7] shadow-xs'
                            : 'bg-[#9A7B56] text-white hover:bg-[#836643] shadow-2xs'
                        }`}
                    >
                      {currentStatus === 'added' && <Check className="w-3.5 h-3.5 stroke-[3px] text-[#2C2A27]" />}
                      {currentStatus === 'added'
                        ? 'Added to Cart'
                        : currentStatus === 'adding'
                          ? 'Processing...'
                          : product.is_active ? 'Add to Cart' : 'Unavailable'}
                    </button>
                  </div>

                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

export default DealsSection;